import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { IUser } from 'src/app/models/user.interface';
import { AuthService } from 'src/app/services/guard/auth.service';
import { UserService } from 'src/app/services/user.service';
import { ModalController } from '@ionic/angular';
import { AlertController } from '@ionic/angular';
import { HelpModalComponent } from '../../modals/help-modal/help-modal.component';
import { AccountService } from 'src/app/services/account.service';
import { IAccount } from 'src/app/models/account.interface';
import { NotificationService } from 'src/app/services/notification.service';
import { MenuVisibilityService } from 'src/app/services/menu-visibility.service';
import { INotification } from 'src/app/models/notification.interface';
import { AiCopilotService } from 'src/app/services/ai-copilot.service';

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    standalone: false
})
export class TopbarComponent implements OnInit, OnDestroy {

  @Input()
  title: string;

  isPopoverOpen = false;
  popoverEvent: any;
  
  isNotificationsOpen = false;
  notificationsEvent: any;
  notifications: INotification[] = [];
  unreadCount = 0;

  isAuthenticated: boolean = false; // Local state to track auth
  private authSub: Subscription; // Subscription to the authState
  user: IUser | null = null;

  profileImage: string | null = null;
  accounts: IAccount[] | null = null;
  activeAccount: IAccount | null = null;
  showMenu: boolean = true;
  private menuSub: any;


  /**
   * Sidebar component
   * @param router router
   * @param vdlIconRegistry VDL icon registry
   */
  constructor(private router: Router,
              private authService: AuthService,
              private userService: UserService,
              private modalCtrl: ModalController,
              private alertCtrl: AlertController,
              private accountService: AccountService,
              private notificationService: NotificationService,
              private menuVisibility: MenuVisibilityService,
              private aiCopilotService: AiCopilotService) {
    this.title = 'social-dash';
  }


  /** Navigate to the accounts landing page and close the popover */
  goToAccounts() {
    try {
      this.router.navigate(['/accounts']);
    } catch (err) {
      console.warn('Failed to navigate to accounts', err);
    }
    this.closePopover();
  }

  async openHelp(topicId?: string) {
    try {
      const modal = await this.modalCtrl.create({
        component: HelpModalComponent,
        componentProps: { topicId }
      });
      await modal.present();
    } catch (err) {
      console.error('Unable to open help modal', err);
    }
  }

  /** Prompt user to create a new account (business users only) */
  async openCreateAccount() {
    this.closePopover();
    try {
      // Tell the UI we are in account-selection/creation mode
      this.accountService.setSelectingAccountMode(true);
      const alert = await this.alertCtrl.create({
        header: 'Create Account',
        inputs: [
          { name: 'name', type: 'text', placeholder: 'Account name' }
        ],
        buttons: [
            { text: 'Cancel', role: 'cancel' },
          {
            text: 'Create',
            handler: async (vals) => {
              try {
                if (!vals || !vals.name || vals.name.trim().length === 0) return false;
                const payload = { name: vals.name.trim() };
                const acct = await firstValueFrom(this.accountService.createAccount(payload));
                if (acct) {
                  // select the new account locally (do not persist from this UI path)
                  try { this.accountService.setActiveAccountLocal(acct); } catch (e) { console.warn('Failed to set local active account after create', e); }
                  // navigate to home/dashboard for the new account
                  window.location.href = '/home';
                  return true;
                } else {
                  this.utilShowError('Failed to create account');
                  return false;
                }
              } catch (err) {
                console.error('Error creating account', err);
                this.utilShowError('Failed to create account');
                return false;
              }
            }
          }
        ]
      });
      await alert.present();
      // After the prompt is dismissed, if user didn't create, ensure mode cleared
      alert.onDidDismiss().then(() => this.accountService.setSelectingAccountMode(false));
    } catch (err) {
      console.error('Failed to open create account prompt', err);
      this.accountService.setSelectingAccountMode(false);
    }
  }

  private utilShowError(msg: string) {
    try { this.alertCtrl.create({ header: 'Error', message: msg, buttons: ['OK'] }).then(a => a.present()); } catch (e) { console.error(msg); }
  }


  /**
   * On page init
   */
  ngOnInit() {
    this.notificationService.notifications$.subscribe(notifications => {
      this.notifications = notifications;
    });

    this.notificationService.unreadCount$.subscribe(count => {
      this.unreadCount = count;
    });

    this.authService.initialized.subscribe((isInitialized) => {
      if (isInitialized) {
        // Subscribe to authentication state
        this.authSub = this.authService.authState.subscribe((state) => {
          this.isAuthenticated = state;

          if (this.isAuthenticated) {
            // Fetch user data and update UI
            this.authService.fetchUser().subscribe({
              next: (user) => {
                this.user = user;
                // fallback profile image for now (may be replaced by account logo below)
                this.profileImage = user?.logo
                  ? this.userService.getUserLogoUrl(user._id, user.logo)
                  : this.userService.getDefaultProfileImage();
              },
              error: (error) => {
                console.error('Error fetching user:', error);
                this.user = null;
                this.profileImage = null;
              },
            });

            // Subscribe to accounts changes (AuthService hydrates accounts from /users/me)
            this.accountService.accountsObservable().subscribe(async (accounts) => {
              this.accounts = accounts;
              // If there is an activeAccountId persisted but activeAccount hasn't been resolved, try to resolve it
              const activeValue = this.accountService.getActiveAccount();
              if (activeValue && activeValue._id && accounts) {
                const match = accounts.find(a => a._id === activeValue._id);
                // Adopt cached active account locally during hydration; do not re-persist to server.
                if (match) try { this.accountService.setActiveAccountLocal(match); } catch (e) { console.warn('Failed to set local active account during accounts hydration', e); }
              }
            });

            // Subscribe to active account and update profile image to use account logo when present
            this.accountService.activeAccountObservable().subscribe((acct) => {
              this.activeAccount = acct;
              const acctLogo = this.accountService.getAccountLogoUrl(acct);
              if (acctLogo) {
                this.profileImage = acctLogo;
              } else {
                // fallback to user logo
                this.profileImage = this.user?.logo ? this.userService.getUserLogoUrl(this.user._id, this.user.logo) : this.userService.getDefaultProfileImage();
              }
            });

          } else {
            // Clear user data if not authenticated
            this.user = null;
            this.profileImage = null;
          }
        });
      }

      // Subscribe to menu visibility
      this.menuSub = this.menuVisibility.state$.subscribe(s => {
        this.showMenu = s.showMenu;
      });
    });
  }
  



  /**
   * On page close
   */
  ngOnDestroy(): void {
    // Clean up the subscription to avoid memory leaks
    if (this.authSub) {
      this.authSub.unsubscribe();
    }
    if (this.menuSub) this.menuSub.unsubscribe();
  }


  /**
   * Open the profile menu
   */
  openProfileMenu(event: Event) {
    this.popoverEvent = event;
    this.isPopoverOpen = true;
  }

  /** Navigate to home (logo click) */
  goHome() {
    try {
      this.router.navigate(['/home']);
    } catch (err) {
      console.warn('Failed to navigate home', err);
      try { window.location.href = '/home'; } catch (e) { /* fallback */ }
    }
  }


  /**
   * Close popover
   */
  closePopover() {
    this.isPopoverOpen = false;
  }


  /**
   * Navigate to settings
   * @param url url
   */
  settings() {
    this.router.navigate(['/settings/user']);
    this.closePopover();
  }


    /**
   * Navigate to settings
   * @param url url
   */
    support() {
      this.router.navigate(['/support/my-tickets']);
      this.closePopover();
    }




  /** Switch account from UI - accepts an account object */
  switchToAccount(account: IAccount): void {
    this.userService.switchAccountAndNavigate(account, '/home').subscribe();
  }


  // Linked-account UI removed — use Accounts feature

  /**
   * Logout
   */
  async logout() {
    await this.authService.logout();
    this.closePopover();
    await this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  // Helper used by the template to decide whether to show "Switch Account"
  get hasSwitchAccountAccess(): boolean {
    const roles: string[] = this.user?.roles ?? [];
    const hasRoleAccess = Array.isArray(roles) && (roles.includes('pro') || roles.includes('admin'));
    const hasLevelAccess = this.user?.userLevel === 'pro';
    return hasRoleAccess || hasLevelAccess;
  }

  /** Show the AI copilot button only on pages where it is available */
  get showAiButton(): boolean {
    const url = this.router.url.split('?')[0];
    return url === '/post-new' || url === '/post-wordpress-new';
  }

  openAiCopilot(): void {
    this.aiCopilotService.triggerOpen();
  }

  openNotifications(event: Event) {
    this.notificationsEvent = event;
    this.isNotificationsOpen = true;
  }

  /** Clear all notifications via the NotificationService */
  clearAllNotifications() {
    try {
      this.notificationService.clearAll();
    } catch (err) {
      console.error('Failed to clear notifications', err);
    }
  }

  closeNotifications() {
    this.isNotificationsOpen = false;
    this.notificationService.markAllAsRead();
  }

  removeNotification(id: string) {
      // Optional: allow removing single notification
  }

  handleNotificationClick(notification: INotification) {
    if (notification.link) {
      this.router.navigateByUrl(notification.link);
    }
    this.closeNotifications();
  }

}
